import { IAccount, PlanNames, TFilingStatusOption } from 'models/account';
import { Routes } from 'fnRoutes';
import { CONSTANTS } from 'constants/common';
import { UserInfo } from 'models/user';
import { Household } from 'models/household';
import { BusinessStatusLabel, BusinessPlan, Business } from 'models/business';
import { Invitation } from 'models/invitations';

export const pathOrder = (pathname: string) => {
  switch (pathname) {
    case Routes.LOGIN:
    case Routes.LOGIN1120S:
    case Routes.LOGINBIB:
    case Routes.LOGOUT:
    case Routes.POST_AUTH_VERIFY:
    case Routes.POST_LOGIN:
    case Routes.POST_SIGNUP:
    case Routes.POST_LOGIN_1120S:
    case Routes.POST_SIGNUP_1120S:
    case Routes.POST_LOGIN_BIB:
    case Routes.POST_SIGNUP_BIB:
    case Routes.FIRST_EXPERIENCE_STEP_1:
    case Routes.FIRST_EXPERIENCE_STEP_2:
    case Routes.FIRST_EXPERIENCE_STEP_3:
    case Routes.FIRST_EXPERIENCE_STEP_4:
    case Routes.FIRST_EXPERIENCE_STEP_5:
    case Routes.FIRST_EXPERIENCE_PLAN_SELECTION:
      return 0;
    case Routes.TERMS_CONDITIONS:
      return 5;
    case Routes.CHURN_ZERO_AUTHENTICATION:
      return 20;
    case Routes.PRICING_TABLE:
    case Routes.PROGRESS_TRACKER:
      return 25;
    default:
      return 30;
  }
};

interface Options {
  currentPath: string;
  account: IAccount | undefined;
  user: UserInfo;
  company: Business | undefined;
  isAdminSpoof: boolean;
  alreadyPaid: boolean;
  household: Household | undefined;
  invitations: Invitation[] | undefined;
}
export class Redirection {
  path: string = '';

  account: IAccount | undefined;

  household: Household | undefined;

  invitations: Invitation[] | undefined;

  user: UserInfo;

  company: Business | undefined;

  isAdminSpoof: boolean;

  alreadyPaid: boolean;

  constructor(
    currentPath: string,
    account: IAccount | undefined,
    user: UserInfo,
    company: Business | undefined,
    isAdminSpoof: boolean = false,
    alreadyPaid: boolean = false,
    household: Household | undefined = undefined,
    invitations: Invitation[] | undefined = undefined,
  ) {
    this.path = currentPath;
    this.account = account;
    this.user = user;
    this.company = company;
    this.isAdminSpoof = isAdminSpoof;
    this.alreadyPaid = alreadyPaid;
    this.household = household;
    this.invitations = invitations;
  }

  apply(func: (options: Options) => string): this {
    this.path = func({
      currentPath: this.path,
      user: this.user,
      account: this.account,
      company: this.company,
      isAdminSpoof: this.isAdminSpoof,
      alreadyPaid: this.alreadyPaid,
      household: this.household,
      invitations: this.invitations,
    });
    return this;
  }

  getPath(): string {
    return this.path;
  }
}

export const wrongHomeRedirect = ({
  currentPath,
  user,
  isAdminSpoof,
}: Options): string => {
  if (
    user.role === CONSTANTS.USER_ROLES.CUSTOMER &&
    currentPath === Routes.ADMIN_HOME
  ) {
    return Routes.CUSTOMER_HOME;
  }
  if (
    user.role !== CONSTANTS.USER_ROLES.CUSTOMER &&
    !isAdminSpoof &&
    currentPath === Routes.CUSTOMER_HOME
  ) {
    return Routes.ADMIN_HOME;
  }
  return currentPath;
};

export const invitationsRedirect = ({
  currentPath,
  invitations,
}: Options): string => {
  if (invitations && invitations.length > 0) {
    return Routes.VIEW_INVITATION;
  }
  return currentPath;
};

export const progressTrackerRedirect = ({
  currentPath,
  user,
  company,
}: Options): string => {
  if (
    user.role === CONSTANTS.USER_ROLES.CUSTOMER &&
    pathOrder(currentPath) > pathOrder(Routes.PROGRESS_TRACKER) &&
    (company?.status == null || company?.status === BusinessStatusLabel.NEW)
  ) {
    // allow progress tracker user access document and my account page
    if (currentPath === Routes.DOCUMENTS || currentPath === Routes.MY_ACCOUNT) {
      return currentPath;
    }
    // Todo:- Remove after new onboarding flow enabled for customers
    if (currentPath === Routes.ACCOUNT_SETTING) {
      return currentPath;
    }
    return Routes.PROGRESS_TRACKER;
  }
  return currentPath;
};

export const taxOnlyCustomerRedirect = ({
  currentPath,
  company,
}: Options): string => {
  const disabledPaths = [
    Routes.TRANSACTIONS,
    Routes.ACCOUNTING_ADD_TRANSACTION,
    Routes.ACCOUNTING_ALL_TRANSACTIONS,
    Routes.ACCOUNTING_UNKNOWN_TRANSACTIONS,
    Routes.TAX_LIABILITY_FORM,
    Routes.PAYROLL,
    Routes.PAYROLL_SETTINGS,
    Routes.PAYROLL_ONBOARDING,
    Routes.TAXES,
  ];
  if (
    company?.formationsPlan === BusinessPlan.TaxOnly &&
    disabledPaths.includes(currentPath)
  ) {
    return Routes.CUSTOMER_HOME;
  }
  return currentPath;
};

export const termNConditionRedirect = ({
  currentPath,
  user,
}: Options): string => {
  const accepted =
    user.lastTncAcceptedVersion != null &&
    new Date(user.lastTncAcceptedVersion).getTime() >=
      new Date(CONSTANTS.TERM_AND_CONDITION_VERSION).getTime();

  if (
    user.role !== CONSTANTS.USER_ROLES.CUSTOMER &&
    currentPath === Routes.TERMS_CONDITIONS
  ) {
    return Routes.ADMIN_HOME;
  }
  if (user.role === CONSTANTS.USER_ROLES.CUSTOMER) {
    if (accepted && currentPath === Routes.TERMS_CONDITIONS) {
      return Routes.CUSTOMER_HOME;
    }
    if (
      !accepted &&
      pathOrder(currentPath) > pathOrder(Routes.TERMS_CONDITIONS)
    ) {
      return Routes.TERMS_CONDITIONS;
    }
  }

  return currentPath;
};

export const getRedirectRouteBaseStatus = (
  isWithinFirstExperiencePath: boolean,
  plan?: BusinessPlan,
  name?: string,
  entityType?: string,
  taxFilingStatus?: TFilingStatusOption,
  acceptedLatestTerm?: boolean,
  isActive?: boolean,
  paidAlready?: boolean,
  recommendedPlan?: PlanNames,
  planSelection?: PlanNames,
): string | undefined => {
  if (isActive) {
    return undefined;
  }
  switch (plan) {
    case BusinessPlan.FullSuite:
      if (isWithinFirstExperiencePath) {
        return undefined;
      }
      if (!name) {
        return Routes.FIRST_EXPERIENCE_STEP_1;
      }
      if (!entityType) {
        return Routes.FIRST_EXPERIENCE_STEP_2;
      }
      if (!taxFilingStatus) {
        return Routes.FIRST_EXPERIENCE_STEP_3;
      }
      if (
        (paidAlready && !acceptedLatestTerm) ||
        (!paidAlready && !recommendedPlan)
      ) {
        return Routes.FIRST_EXPERIENCE_STEP_4;
      }
      if (
        !paidAlready &&
        !acceptedLatestTerm &&
        recommendedPlan &&
        !planSelection
      ) {
        return Routes.FIRST_EXPERIENCE_STEP_5;
      }
      if (
        !paidAlready &&
        acceptedLatestTerm &&
        recommendedPlan &&
        !planSelection
      ) {
        return Routes.FIRST_EXPERIENCE_PLAN_SELECTION;
      }
      return undefined;
    case BusinessPlan.BIB:
      if (isWithinFirstExperiencePath) {
        return undefined;
      }
      if (!name) {
        return Routes.FIRST_EXPERIENCE_STEP_1;
      }
      if (!entityType) {
        return Routes.FIRST_EXPERIENCE_STEP_2;
      }
      if (!taxFilingStatus) {
        return Routes.FIRST_EXPERIENCE_STEP_3;
      }
      if (!acceptedLatestTerm) {
        return Routes.FIRST_EXPERIENCE_STEP_4;
      }
      return undefined;
    case BusinessPlan.TaxOnly:
      if (isWithinFirstExperiencePath) {
        return undefined;
      }
      if (!name || !entityType) {
        return Routes.FIRST_EXPERIENCE_STEP_1;
      }
      if (!taxFilingStatus) {
        return Routes.FIRST_EXPERIENCE_STEP_2;
      }
      if (!acceptedLatestTerm) {
        return Routes.FIRST_EXPERIENCE_STEP_3;
      }
      return undefined;
    default:
      if (isWithinFirstExperiencePath) {
        return undefined;
      }
      // this means the company is not yet created, so we need to check if user is created
      if (name) {
        return Routes.FIRST_EXPERIENCE_STEP_2;
      }
      return Routes.FIRST_EXPERIENCE_STEP_1;
  }
};

export const firstExperienceRedirect = ({
  account,
  currentPath,
  user,
  company,
  alreadyPaid,
  household,
}: Options): string => {
  const accepted =
    user.lastTncAcceptedVersion != null &&
    new Date(user.lastTncAcceptedVersion).getTime() >=
      new Date(CONSTANTS.TERM_AND_CONDITION_VERSION).getTime();
  if (
    user.role !== CONSTANTS.USER_ROLES.CUSTOMER &&
    currentPath === Routes.TERMS_CONDITIONS
  ) {
    return Routes.ADMIN_HOME;
  }

  if (user.role === CONSTANTS.USER_ROLES.CUSTOMER) {
    const isWithinFirstExperiencePath =
      currentPath === Routes.FIRST_EXPERIENCE_STEP_1 ||
      currentPath === Routes.FIRST_EXPERIENCE_STEP_2 ||
      currentPath === Routes.FIRST_EXPERIENCE_STEP_3 ||
      currentPath === Routes.FIRST_EXPERIENCE_STEP_4 ||
      currentPath === Routes.FIRST_EXPERIENCE_STEP_5 ||
      currentPath === Routes.FIRST_EXPERIENCE_PLAN_SELECTION;

    const redirectRoute = getRedirectRouteBaseStatus(
      isWithinFirstExperiencePath,
      company?.formationsPlan,
      user.name?.first ?? user.name?.last,
      company?.entityType,
      household?.filingStatus,
      !!user.lastTncAcceptedVersion,
      company?.status === BusinessStatusLabel.ACTIVE,
      alreadyPaid,
      account?.planRecommendation?.recommendedPlan,
      account?.planSelection,
    );
    if (redirectRoute && redirectRoute !== currentPath) {
      return redirectRoute;
    }
    if (accepted && currentPath === Routes.TERMS_CONDITIONS) {
      return Routes.CUSTOMER_HOME;
    }
    // if renewed terms, redirect Terms
    if (
      !accepted &&
      pathOrder(currentPath) > pathOrder(Routes.TERMS_CONDITIONS)
    ) {
      return Routes.TERMS_CONDITIONS;
    }
  }
  return currentPath;
};

const staffRoles: string[] = [
  CONSTANTS.USER_ROLES.ADMIN,
  CONSTANTS.USER_ROLES.SUPER_ADMIN,
  CONSTANTS.USER_ROLES.SUCCESS,
  CONSTANTS.USER_ROLES.FULFILLMENT,
  CONSTANTS.USER_ROLES.CUSTOMER_SERVICE,
];

export const isRoleNotAllowed = (
  allowedRoles: string[],
  role: string,
  currentPath: string,
) => {
  const hasAny = allowedRoles.includes('any');
  const isAllowed = allowedRoles.includes(role);
  const isStaffAllowed =
    allowedRoles.includes('staff') && staffRoles.includes(role);
  const homeUrl =
    role === CONSTANTS.USER_ROLES.CUSTOMER
      ? Routes.CUSTOMER_HOME
      : Routes.ADMIN_HOME;
  if (hasAny || isAllowed || isStaffAllowed) {
    return false;
  }
  if (homeUrl === currentPath) {
    return false;
  }
  return true;
};
